import {
    OrgEmployeeRequestService,
    type Status,
    type OrgEmployeeRequest,
} from '@api/OrgEmployeeRequestService';
import { useCallback, useState } from 'react';
import { invalidateOrgEmployeeRequestData } from '@stores/orgEmployeeRequestStore';

// TODO: Use useApiCall hook
const useOrgEmployeeRequestStatusUpdate = (
    orgId: string,
    orgEmployeeId: string | undefined,
): {
    updatedOrgEmployeeRequest: OrgEmployeeRequest | undefined;
    isOrgEmployeeRequestStatusUpdateInProgress: boolean;
    updateOrgEmployeeRequestStatus: (
        requestId: string,
        status: Status,
        comment?: string,
    ) => void;
    error: string | undefined;
} => {
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [updatedOrgEmployeeRequest, setUpdatedOrgEmployeeRequest] =
        useState<OrgEmployeeRequest>();
    const [error, setError] = useState<string>();

    /*
     * This effect handles the sequential API calls:
     * 1. First post the comment (if it exists)
     * 2. Then update the status
     */
    const performStatusUpdate = useCallback(
        async (requestId: string, status: Status, comment?: string) => {
            if (!orgEmployeeId) {
                return;
            }

            setIsUpdating(true);
            setError(undefined);

            try {
                // Step 1: Put comment if it exists
                if (comment) {
                    await OrgEmployeeRequestService.putOrgEmployeeRequestComment(
                        orgId,
                        orgEmployeeId,
                        requestId,
                        { value: comment },
                    );
                }

                // Step 2: Update status
                const response =
                    await OrgEmployeeRequestService.putOrgEmployeeRequestStatus(
                        orgId,
                        orgEmployeeId,
                        requestId,
                        { value: status },
                    );
                setUpdatedOrgEmployeeRequest(response);

                // The mutation changed request data — drop cached reads
                invalidateOrgEmployeeRequestData();
            } catch (err: unknown) {
                setError(
                    (err as Error)?.message ??
                        'Something went wrong while executing the operation.',
                );
            } finally {
                setIsUpdating(false);
            }
        },
        [orgId, orgEmployeeId],
    );

    const updateOrgEmployeeRequestStatus = useCallback(
        (requestId: string, status: Status, comment?: string) => {
            performStatusUpdate(requestId, status, comment);
        },
        [performStatusUpdate],
    );

    return {
        updatedOrgEmployeeRequest,
        isOrgEmployeeRequestStatusUpdateInProgress: isUpdating,
        updateOrgEmployeeRequestStatus,
        error,
    };
};

export default useOrgEmployeeRequestStatusUpdate;
